import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { api, daysAgo, today } from "@/lib/api";
import { Btn, Card, DataTable, Field, Select } from "@/components/shared";
import { Chip, money, call, Modal, ReasonDialog, MemberPicker, ExportBtn } from "./common";
import { ItemsTable } from "./Items";

const FIELD_LABEL = { date: "Date *", utr: "UTR / RRN", reference: "Reference / Txn ID", credit: "Credit amount", debit: "Debit amount", amount: "Amount (single column)", drcr: "Dr/Cr indicator", narration: "Narration", balance: "Balance", charge: "Charge / fee", status: "Status" };

export function StatementUpload({ source, sources, onDone }) {
  const [sid, setSid] = useState("");
  const [file, setFile] = useState(null);
  const [up, setUp] = useState(null);
  const [map, setMap] = useState({});
  const [busy, setBusy] = useState(false);
  const [inputKey, setInputKey] = useState(0);
  const parse = async () => {
    setBusy(true);
    const fd = new FormData();
    fd.append("source", source); fd.append("source_id", sid); fd.append("file", file);
    const r = await call(() => api.post("/admin/recon/statements/upload", fd));
    setBusy(false);
    if (r) { setUp(r); setMap(r.mapping); }
  };
  const commit = async () => {
    setBusy(true);
    const r = await call(() => api.post(`/admin/recon/statements/${up.upload_id}/commit`, { mapping: map }));
    setBusy(false);
    if (r) { toast.success(`${r.inserted} new lines · ${r.duplicates} already uploaded (skipped) · ${r.skipped} blank`); setUp(null); setFile(null); setInputKey((k) => k + 1); onDone?.(); }
  };
  return (
    <div className="flex flex-wrap items-end gap-3" data-testid={`stmt-upload-${source}`}>
      <Field label={source === "bank" ? "Company bank" : "Vendor"}><Select options={[{ value: "", label: "Select" }, ...sources]} value={sid} onChange={(e) => setSid(e.target.value)} data-testid={`stmt-${source}-source`} /></Field>
      <Field label="Statement (Excel .xls / .xlsx / CSV)"><input key={inputKey} type="file" accept=".xls,.xlsx,.xlsm,.csv" className="np-input" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid={`stmt-${source}-file`} /></Field>
      <Btn onClick={parse} loading={busy} disabled={!sid || !file} data-testid={`stmt-${source}-parse`}><Upload className="h-4 w-4" />Upload</Btn>
      <Modal open={!!up} onClose={() => setUp(null)} title="Map statement columns" testid="stmt-mapping-dialog" wide>
        {up && (
          <div className="space-y-3">
            <div className="text-xs text-slate-600">{up.row_count} rows found. {up.mapping_saved ? <b className="text-emerald-700">Saved mapping for this account applied — check and confirm.</b> : "First upload for this account — choose which column is which. The mapping will be saved."}</div>
            <div className="grid gap-2 sm:grid-cols-3">
              {up.fields.map((k) => (
                <Field key={k} label={FIELD_LABEL[k]}>
                  <Select options={[{ value: "", label: "— not in file —" }, ...up.headers.map((h) => ({ value: h, label: h }))]} value={map[k] || ""} onChange={(e) => setMap({ ...map, [k]: e.target.value })} data-testid={`map-${k}`} />
                </Field>
              ))}
            </div>
            <div className="overflow-x-auto rounded border border-slate-200 text-[11px]">
              <table><thead className="bg-slate-50"><tr>{up.headers.map((h) => <th key={h} className="px-2 py-1 text-left">{h}</th>)}</tr></thead>
                <tbody>{up.preview.map((r, i) => <tr key={i} className="border-t">{r.map((c, j) => <td key={j} className="whitespace-nowrap px-2 py-1">{c}</td>)}</tr>)}</tbody></table>
            </div>
            <Btn onClick={commit} loading={busy} disabled={!map.date || !(map.credit || map.debit || map.amount)} data-testid="stmt-commit">Save mapping & import</Btn>
          </div>
        )}
      </Modal>
    </div>
  );
}

const QUEUES = ["q1_bank_missing", "q2_request_missing", "q3_mismatch", "q4_wallet_pending", "q5_bank_pending"];

function Queues({ meta }) {
  const [kind, setKind] = useState(QUEUES[0]);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const load = () => api.get("/admin/recon/items", { params: { area: "evalue", status: "OPEN" } }).then(({ data }) => {
    setCounts(data.reduce((a, r) => ({ ...a, [r.kind]: (a[r.kind] || 0) + 1 }), {}));
    setRows(data.filter((r) => r.kind === kind));
  });
  useEffect(() => { load(); }, [kind]); // eslint-disable-line
  return (
    <Card title="E-value open queues" className="mt-6">
      <div className="mb-3 flex flex-wrap gap-2">
        {QUEUES.map((q, i) => (
          <button key={q} onClick={() => setKind(q)} className={`rounded-md border px-3 py-1.5 text-xs font-semibold ${kind === q ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white"}`} data-testid={`evalue-queue-${q}`}>
            {i + 1}. {meta.kinds[`evalue:${q}`]} <span className="ml-1 rounded bg-red-100 px-1 text-red-700">{counts[q] || 0}</span>
          </button>
        ))}
      </div>
      <ItemsTable rows={rows} reload={load} meta={meta} compact />
    </Card>
  );
}

export function EValue({ meta }) {
  const [f, setF] = useState({ bank_id: "", verification: "", q: "", date_from: daysAgo(30), date_to: today() });
  const [member, setMember] = useState(null);
  const [rows, setRows] = useState([]);
  const [lines, setLines] = useState([]);
  const [unlinked, setUnlinked] = useState(true);
  const [link, setLink] = useState(null);
  const [reqId, setReqId] = useState("");
  const [tick, setTick] = useState(0);
  const params = () => ({ ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)), member: member?.id });
  const load = () => {
    api.get("/admin/recon/evalue", { params: params() }).then(({ data }) => setRows(data));
    api.get("/admin/recon/lines", { params: { source: "bank", source_id: f.bank_id || undefined, unlinked } }).then(({ data }) => setLines(data));
    setTick((t) => t + 1);
  };
  useEffect(() => { load(); }, [unlinked]); // eslint-disable-line
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const banks = meta.banks.map((b) => ({ value: b.id, label: b.label }));
  const cols = [
    { label: "Request", key: "req_id", render: (r) => <span className="font-mono text-xs">{r.req_id}</span> },
    { label: "Merchant", key: "login_id", render: (r) => <span className="font-mono text-xs">{r.login_id}</span> },
    { label: "Amount", key: "amount", render: (r) => <span className="font-mono">{money(r.amount)}</span> },
    { label: "UTR", key: "utr", render: (r) => <span className="font-mono text-xs">{r.utr}</span> },
    { label: "Transfer date", key: "deposit_date" }, { label: "Receiving bank", key: "bank_name" },
    { label: "Type", csv: (r) => (r.is_credit ? "Credit" : "E-value"), render: (r) => (r.is_credit ? <Chip s="PARTIAL" testid="type-credit" /> : <span className="text-xs">E-value</span>) },
    { label: "Request status", key: "status", render: (r) => <Chip s={r.status} /> },
    { label: "Bank verification", key: "bank_verification", render: (r) => <Chip s={r.bank_verification} testid={`fr-bankver-${r.req_id}`} /> },
    { label: "Wallet posting", key: "wallet_posting", render: (r) => <Chip s={r.wallet_posting} testid={`fr-posting-${r.req_id}`} /> },
    { label: "Fully reconciled", csv: (r) => (r.recon_full ? "Yes" : "No"), render: (r) => (r.recon_full ? <Chip s="MATCHED" testid={`fr-full-${r.req_id}`} /> : <span className="text-xs text-slate-400">No</span>) },
  ];
  const lcols = [
    { label: "Date", key: "date" }, { label: "UTR", key: "utr", render: (r) => <span className="font-mono text-xs">{r.utr || "—"}</span> },
    { label: "Credit", key: "credit", render: (r) => <span className="font-mono">{money(r.credit)}</span> }, { label: "Debit", key: "debit", render: (r) => <span className="font-mono">{money(r.debit)}</span> },
    { label: "Narration", key: "narration", render: (r) => <div className="max-w-[300px] truncate text-xs">{r.narration}</div> },
    { label: "Linked", csv: (r) => r.linked_fr || r.linked_vf || "", render: (r) => (r.linked_fr || r.linked_vf ? <Chip s="MATCHED" /> : <Chip s="OPEN" />) },
    { label: "Action", render: (r) => !r.linked_fr && !r.linked_vf && r.credit > 0 && <button className="text-xs font-semibold text-blue-700" onClick={() => { setReqId(""); setLink(r); }} data-testid={`line-link-${r.id}`}>Link to request</button> },
  ];
  const doLink = async (b) => { if (await call(() => api.post("/admin/recon/evalue/link", { line_id: link.id, req_id: reqId.trim(), ...b }), "Linked & bank verified")) { setLink(null); load(); } };
  return (
    <div>
      <Card title="Upload company bank statement"><StatementUpload source="bank" sources={banks} onDone={load} /></Card>
      <div className="np-card my-4 flex flex-wrap items-end gap-3 p-4">
        <Field label="Bank"><Select options={[{ value: "", label: "All" }, ...banks]} value={f.bank_id} onChange={set("bank_id")} data-testid="evalue-bank" /></Field>
        <Field label="Bank verification"><Select options={[["", "All"], ["PENDING", "Pending"], ["VERIFIED", "Verified"], ["MISMATCH", "Mismatch"], ["NA", "N/A (credit)"]].map(([value, label]) => ({ value, label }))} value={f.verification} onChange={set("verification")} data-testid="evalue-verification" /></Field>
        <MemberPicker value={member} onChange={setMember} testid="evalue-member" />
        <Field label="Reference / UTR"><input className="np-input w-40" value={f.q} onChange={set("q")} data-testid="evalue-q" /></Field>
        <Field label="From"><input type="date" className="np-input" value={f.date_from} onChange={set("date_from")} /></Field>
        <Field label="To"><input type="date" className="np-input" value={f.date_to} onChange={set("date_to")} /></Field>
        <Btn onClick={load} data-testid="evalue-search">Search</Btn>
        <div className="ml-auto"><ExportBtn name="evalue-requests" rows={rows} cols={cols} /></div>
      </div>
      <DataTable testid="evalue-table" rows={rows} columns={cols} empty="No fund requests" />
      <Card className="mt-6" title="Bank statement lines" action={<div className="flex items-center gap-3"><label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={unlinked} onChange={(e) => setUnlinked(e.target.checked)} data-testid="lines-unlinked" />Unlinked only</label><ExportBtn name="bank-lines" rows={lines} cols={lcols} /></div>}>
        <DataTable testid="bank-lines-table" rows={lines} columns={lcols} empty="No statement lines" />
      </Card>
      <Queues key={tick} meta={meta} />
      <ReasonDialog open={!!link} onClose={() => setLink(null)} title="Link bank credit to fund request" onSubmit={doLink} testid="link-dialog" cta="Link">
        {link && <div className="text-xs">Bank credit <b>{money(link.credit)}</b> · UTR {link.utr || "—"} · {link.date}</div>}
        <Field label="Fund request ID"><input className="np-input font-mono" value={reqId} onChange={(e) => setReqId(e.target.value)} placeholder="FR..." data-testid="link-req-id" /></Field>
      </ReasonDialog>
    </div>
  );
}

